<!--
  @component DatabaseScreen — "Database" section (per-project, scope=one).
  tier:      screen
  scope:     one project's database — provision a managed Postgres, attach it
             to the app, see the connection, and see which OTHER projects
             share it (the Phase 2 M:N "shared with"). Manage sharing across a
             set from the all-projects Database view.
  reads:     useDeploymentIO (db.list / db.provision / db.connect / db.listLinks),
             useIdentityIO (currentProject), useScopeIO (jump to aggregate)
-->
<script lang="ts">
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useDeploymentIO } from "../../../../global/io/nodes/deployment/DeploymentIO.svelte";
  import { useScopeIO } from "./ScopeIO.svelte";
  import Card from "../../../../global/visuals/primitives/Card.svelte";
  import StatusNote from "../../../../global/visuals/primitives/StatusNote.svelte";
  import ConnectionCard from "../../../../global/visuals/primitives/ConnectionCard.svelte";
  import Skeleton from "../../../../global/visuals/primitives/Skeleton.svelte";

  const identity = useIdentityIO();
  const node = useDeploymentIO();
  const scope = useScopeIO();

  const project = $derived(identity.currentProject);

  interface ServerDatabase { id: string; status: "provisioning" | "ready" | "failed" | "destroying" | "destroyed"; engine: string; region: string; internalHost: string; connectionUrlRedacted: string | null; }
  interface Link { projectId: string; projectName: string; secretName: string; }

  let databases = $state<ServerDatabase[]>([]);
  let links = $state<Link[]>([]);
  let busy = $state(false);
  let err = $state<string | null>(null);

  const activeDb = $derived(databases.find((d) => d.status === "ready" || d.status === "provisioning") ?? null);
  // Other projects sharing this database (exclude the current project).
  const sharedWith = $derived(links.filter((l) => l.projectId !== project?.id));

  async function refresh(): Promise<void> {
    if (!project) return;
    try { databases = (await node.dispatch("db.list", { target: { id: project.id } })) as ServerDatabase[] ?? []; }
    catch { databases = []; }
  }
  async function refreshLinks(): Promise<void> {
    const db = activeDb;
    if (!db || db.status !== "ready") { links = []; return; }
    try { const r = (await node.dispatch("db.listLinks", { databaseId: db.id })) as { links: Link[] }; links = r.links ?? []; }
    catch { links = []; }
  }
  $effect(() => { if (project) void refresh(); });
  $effect(() => { if (activeDb?.status === "ready") void refreshLinks(); });

  async function provision(): Promise<void> {
    if (!project) return;
    busy = true; err = null;
    try { await node.dispatch("db.provision", { target: { id: project.id } }); }
    catch (e) { err = e instanceof Error ? e.message : String(e); }
    finally { busy = false; void refresh(); }
  }
  async function attach(): Promise<void> {
    if (!project) return;
    busy = true; err = null;
    try { await node.dispatch("db.connect", { target: { id: project.id } }); }
    catch (e) { err = e instanceof Error ? e.message : String(e); }
    finally { busy = false; void refreshLinks(); }
  }
</script>

<div class="screen">
  <header class="head">
    <h1>Database</h1>
    <p class="sub">A managed Postgres database for your app — provisioned on dedicated infrastructure, with credentials handled for you. Share one database across several projects from the all-projects view.</p>
  </header>

  <Card eyebrow="Managed Postgres" title="Database">
    {#if !project}
      <p class="muted">No project selected.</p>
    {:else if !activeDb}
      <div class="setup">
        <p class="setup-title">Set up a database</p>
        <p class="setup-help">A managed Postgres database for your app — provisioned on dedicated infrastructure with credentials handled for you.</p>
        <button type="button" class="btn-primary" disabled={busy} onclick={() => void provision()}>{busy ? "Setting up…" : "Set up database"}</button>
        {#if err}<p class="err">{err}</p>{/if}
      </div>
    {:else if activeDb.status === "provisioning"}
      <div class="provisioning"><Skeleton variant="text" rows={2} /><p class="muted">Provisioning your database…</p></div>
    {:else}
      <div class="db-head">
        <div class="db-status"><span class="dot live"></span><span>Connected · {activeDb.engine} · {activeDb.region}</span></div>
        <button type="button" class="btn-secondary" disabled={busy} onclick={() => void attach()}>{busy ? "Attaching…" : "Attach to app"}</button>
      </div>
      {#if activeDb.connectionUrlRedacted}
        <div class="conn-wrap"><ConnectionCard title="How your app reaches the database" fields={[{ label: "DATABASE_URL", value: activeDb.connectionUrlRedacted, secret: true, mono: true }, { label: "Host", value: activeDb.internalHost, mono: true }]} /></div>
      {/if}
      {#if err}<p class="err">{err}</p>{/if}

      <!-- Phase 2 M:N — who shares this database -->
      <div class="shared">
        <p class="shared-title">Shared with</p>
        {#if sharedWith.length === 0}
          <p class="muted">Used only by this project. <button type="button" class="link" onclick={() => scope.setAll()}>Share across projects →</button></p>
        {:else}
          <div class="chips">
            {#each sharedWith as l (l.projectId)}<span class="chip">{l.projectName}</span>{/each}
          </div>
          <p class="muted"><button type="button" class="link" onclick={() => scope.setAll()}>Manage sharing →</button></p>
        {/if}
      </div>

      <div class="collections">
        <p class="collections-title">Collections</p>
        <StatusNote tone="soon" message="Browse and manage your tables and content here — coming with the full data experience." />
      </div>
    {/if}
  </Card>
</div>

<style>
  .screen { display: flex; flex-direction: column; gap: var(--space-5); max-width: 980px; font-family: var(--type-font); }
  .head h1 { margin: 0 0 var(--space-2); font-size: 24px; font-weight: 600; letter-spacing: -0.025em; color: var(--text-primary); }
  .sub { margin: 0; font-size: 13.5px; color: var(--text-secondary); line-height: 1.6; max-width: 680px; }
  .muted { margin: var(--space-2) 0 0; font-size: 13px; color: var(--text-tertiary); }
  .err { margin: var(--space-2) 0 0; font-size: 12.5px; color: var(--error); }

  .setup { display: flex; flex-direction: column; gap: var(--space-2); align-items: flex-start; padding: var(--space-2) 0; }
  .setup-title { margin: 0; font-size: 14.5px; font-weight: 600; color: var(--text-primary); }
  .setup-help { margin: 0 0 var(--space-2); font-size: 13px; color: var(--text-secondary); line-height: 1.6; max-width: 560px; }
  .provisioning { display: flex; flex-direction: column; gap: var(--space-2); }

  .db-head { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); margin-bottom: var(--space-3); }
  .db-status { display: inline-flex; align-items: center; gap: var(--space-2); font-size: 13px; color: var(--text-secondary); }
  .dot { width: 6px; height: 6px; border-radius: var(--radius-pill); background: var(--text-tertiary); }
  .dot.live { background: var(--success); }
  .conn-wrap { margin-top: var(--space-2); }

  .shared { margin-top: var(--space-4); }
  .shared-title { margin: 0 0 var(--space-2); font-size: 10.5px; font-weight: 500; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-tertiary); }
  .chips { display: flex; flex-wrap: wrap; gap: var(--space-2); }
  .chip { font-size: 12px; padding: 3px var(--space-2); border-radius: var(--radius-pill); background: var(--bg-hover); color: var(--text-secondary); border: 0.5px solid var(--border-subtle); }
  .link { appearance: none; background: none; border: 0; padding: 0; font: inherit; font-size: 12.5px; color: var(--accent, #5b8cff); cursor: pointer; }
  .link:hover { text-decoration: underline; }

  .collections { margin-top: var(--space-4); }
  .collections-title { margin: 0 0 var(--space-2); font-size: 10.5px; font-weight: 500; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-tertiary); }

  .btn-primary, .btn-secondary { appearance: none; border-radius: var(--radius-md); padding: 0 var(--space-4); height: 36px; font: inherit; font-size: 13px; font-weight: 500; cursor: pointer; white-space: nowrap; transition: background var(--ease-fast), border-color var(--ease-fast); }
  .btn-primary { background: var(--bg-active); color: var(--text-primary); border: 0.5px solid var(--border-strong); }
  .btn-primary:hover:not(:disabled) { background: var(--bg-selected); border-color: var(--border-focus); }
  .btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }
  .btn-secondary { background: transparent; color: var(--text-secondary); border: 0.5px solid var(--border); }
  .btn-secondary:hover:not(:disabled) { background: var(--bg-hover); color: var(--text-primary); }
  .btn-secondary:disabled { opacity: 0.45; cursor: not-allowed; }
</style>
